import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useNavigate, useLocation } from "react-router";
import "./home.css";
import { Navbar } from "../../components/layout/Navbar";
import MobileNavbar from "../../components/layout/MobileNavbar";
import MobileFooter, { MobileFooterTab } from "../../components/layout/MobileFooter";
import { Footer } from "../../components/layout/Footer";
import { Drawer } from "../../components/layout/Drawer";
import ConsentForm from "../../components/Disclaimers/ConsentForm";
import AutoLogout from "../../components/timeout/AutoLogout";
import {
  consentAcceptedAtomFamily,
  izProviderConfigsAtom,
  loadableCompleteVerifiedRecordsAtom,
  loadableIzSearchesAtom,
  userAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import { useBranding } from "../../branding/useBranding";
import ScrollToTop from "../../components/ScrollToTop";
import { Capacitor } from "@capacitor/core";
import { getAPIClient } from "../../apiClient";

const DRAWER_ID = "docket-drawer";

const MOBILE_NAVBAR_DENYLIST = ["/home/search/results"];

const MOBILE_FOOTER_DENYLIST = [
  "/home/settings",
  "/home/settings/profile",
  "/home/settings/notifications",
  "/home/settings/phone",
  "/home/settings/pin",
  "/home/discover",
  "/home/covid-policy",
  "/home/about",
  "/home/records-policy",
  "/home/privacy",
  "/home/terms",
  "/home/feedback",
];

export async function loader() {
  const settings = {};
  return { settings };
}

export function Home() {
  const [currentTab, setCurrentTab] = useState<MobileFooterTab>(MobileFooterTab.SEARCH);
  const [showConsent, setShowConsent] = useState<boolean | undefined>();
  const [gaveConsent, setGaveConsent] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [user] = useAtom(userAtom);
  const [providerConfigs, setProviderConfigs] = useAtom(izProviderConfigsAtom);
  const [consentAccepted, setConsentAccepted] = useAtom(consentAcceptedAtomFamily(userEmail ?? ""));
  const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);
  const [allSearches] = useAtom(loadableIzSearchesAtom);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getAPIClient()
      .getProviderConfigs()
      .then((response) => {
        if (response?.configs) {
          setProviderConfigs(response.configs);
        }
      });
  }, []);

  useEffect(() => {
    if (location.pathname === "/home") {
      navigate(currentTab === MobileFooterTab.SEARCH ? "/home/search" : "/home/records");
    }
  }, [location.pathname, currentTab]);

  useEffect(() => {
    if (!userEmail) {
      setUserEmail(user?.email);
    } else {
      const fetchConsentStatus = async () => {
        const status = consentAccepted;
        setGaveConsent(!!status);
        setShowConsent(!status);
      };

      fetchConsentStatus();
    }

    // This is the absolute bare minimum information for a user to use the app.
    // If any of these fields are missing, most likely the user skipped onboarding
    // and manually navigated to the home screen route
    if (user && (!user.first_name || !user.last_name || !user.legal_sex)) {
      navigate("/signup/accountInfo");
    }
  }, [userEmail, user, consentAccepted]);
  const [pullToRefreshElement, setPullToRefreshElement] = useState<HTMLDivElement | null>(null);

  const handleConsentChange = async (isChecked: boolean) => {
    setGaveConsent(isChecked);
  };

  const navigateToSearchForm = () => {
    setConsentAccepted(true);
    setShowConsent(false);
    navigate("/home/search/newsearch");
  };

  const onTabChange = (tab: MobileFooterTab) => {
    setCurrentTab(tab);
  };

  return (
    <div
      className="drawer flex flex-col h-screen overflow-y-scroll md:dark:bg-dark-desktop-outer max-md:dark:bg-dark"
      ref={setPullToRefreshElement}
    >
      {!Capacitor.isNativePlatform() && <AutoLogout />}
      <ScrollToTop scrollElement={pullToRefreshElement} />
      {showConsent && (
        <ConsentForm
          handleConsentChange={handleConsentChange}
          email={userEmail as string}
          closeModal={navigateToSearchForm}
          gaveConsent={gaveConsent}
        />
      )}
      <input id={DRAWER_ID} type="checkbox" className="drawer-toggle" />
      <div className="drawer-content max-h-screen" data-testid="home-content">
        {!MOBILE_NAVBAR_DENYLIST.includes(location?.pathname) && (
          <MobileNavbar drawerId={DRAWER_ID} />
        )}
        <div className="max-md:hidden">
          <Navbar />
        </div>
        <div className="docket-main-card flex min-h-[calc(100vh-230px)] max-md:mt-16">
          <div className="md:flex-1"></div>
          <div className="flex-6 bg middle-column md:py-6 max-md:pb-6 md:px-6 max-md:max-w-screen">
            <div className="md:bg-white md:dark:bg-dark md:p-10 rounded-lg">
              <Outlet
                context={{ scrollElement: pullToRefreshElement, allVerifiedRecords, allSearches }}
              />
            </div>
          </div>
          <div className="md:flex-1"></div>
        </div>
        {!MOBILE_FOOTER_DENYLIST.includes(location?.pathname) && (
          <MobileFooter onTabChange={onTabChange} />
        )}
        <Footer />
      </div>
      <Drawer id={DRAWER_ID} />
    </div>
  );
}

export default Home;
